import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface Project {
  id: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  description_en: string;
  description_ps: string;
  description_da: string;
  category: string;
  status: string;
  image_url: string;
  location: string;
  beneficiaries: number;
  progress: number;
  created_at: string;
}

const categoryColors: Record<string, string> = {
  Education: "bg-amber-100 text-amber-700",
  Infrastructure: "bg-sky-100 text-sky-700",
  Healthcare: "bg-rose-100 text-rose-700",
  "Emergency Relief": "bg-orange-100 text-orange-700",
  "Women Empowerment": "bg-violet-100 text-violet-700",
};

function getLocalizedField(project: Project, field: string, lang: string): string {
  const suffix = lang === "ps" ? "_ps" : lang === "da" ? "_da" : "_en";
  return (project as any)[field + suffix] || (project as any)[field + "_en"] || "";
}

export default function ProjectsPreview() {
  const { t, i18n } = useTranslation();
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadProjects() {
      try {
        const { data, error: fetchErr } = await supabase
          .from("projects")
          .select("*")
          .in("status", ["active", "completed"])
          .order("created_at", { ascending: false })
          .limit(3);

        if (fetchErr) throw fetchErr;
        setProjects((data as Project[]) ?? []);
      } catch (err: any) {
        setError(err?.message || "Failed to load projects");
      } finally {
        setLoading(false);
      }
    }

    loadProjects();
  }, []);

  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            const children = Array.from(entry.target.children) as HTMLElement[];
            children.forEach((child) => child.classList.add("reveal-visible"));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (headerRef.current) {
      headerRef.current.classList.add("reveal");
      observer.observe(headerRef.current);
    }

    if (gridRef.current) {
      const children = Array.from(gridRef.current.children) as HTMLElement[];
      children.forEach((child, i) => {
        child.classList.add("reveal-child");
        child.style.transitionDelay = `${i * 150}ms`;
      });
      observer.observe(gridRef.current);
    }

    return () => observer.disconnect();
  }, [loading, projects]);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12" ref={headerRef}>
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-500 mb-3 block">
            {t("projects.tag")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
            {t("projects.title")}
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto">{t("projects.subtitle")}</p>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-foreground-500 text-sm mt-3">{t("common.loading")}</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50">
              <i className="ri-error-warning-line text-2xl text-red-400"></i>
            </div>
            <p className="text-foreground-700 font-medium mb-1">{t("projectsPage.errorTitle")}</p>
            <p className="text-foreground-500 text-sm mb-5">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-full font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i> {t("common.retry")}
            </button>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-[#3B9EF5]/10">
              <i className="ri-folder-open-line text-2xl text-secondary-500"></i>
            </div>
            <p className="text-foreground-700 font-medium mb-1">{t("projectsPage.emptyTitle")}</p>
            <p className="text-foreground-500 text-sm mb-5">{t("projectsPage.emptySubtitle")}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-10" ref={gridRef}>
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#3B9EF5]/40 transition-all duration-300 group flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={getLocalizedField(project, "title", i18n.language)}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          categoryColors[project.category] || "bg-[#3B9EF5]/10 text-[#3B9EF5]"
                        }`}
                      >
                        {project.category}
                      </span>
                    </div>
                    {project.status === "completed" && (
                      <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                        {t("projects.completed")}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-foreground-950 text-base mb-2 leading-tight">
                      {getLocalizedField(project, "title", i18n.language)}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-4 line-clamp-2">
                      {getLocalizedField(project, "description", i18n.language)}
                    </p>

                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-foreground-500 mb-1.5">
                        <span>
                          {t("projects.raised")}:{" "}
                          <strong className="text-foreground-950">{project.progress}%</strong>
                        </span>
                        <span className="font-semibold text-primary-500">{project.beneficiaries.toLocaleString()} {t("projects.beneficiaries")}</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#3B9EF5] rounded-full transition-all duration-700"
                          style={{ width: `${Math.min(project.progress, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-foreground-500 text-xs">
                        <i className="ri-map-pin-line"></i>
                        <span>{project.location}</span>
                      </div>
                      <Link
                        to={`/projects/${project.id}`}
                        className="text-primary-500 text-sm font-semibold hover:text-primary-600 cursor-pointer flex items-center gap-1 whitespace-nowrap"
                      >
                        {t("projects.learnMore")} <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#1B2838] text-[#1B2838] rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-[#1B2838] hover:text-white transition-all"
              >
                {t("projects.viewAll")}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}